<template>
  <v-app id="app-translate-ai" class="container-fluid">

    <!-- Titre principal -->
    <div class="row"
      style="margin-top: 0 !important; margin-bottom: 0 !important; padding-top: 0 !important; padding-bottom: 0 !important;">
      <div class="col-12 text-center"
        style="margin-top: 0 !important; margin-bottom: 0 !important; padding-top: 0 !important; padding-bottom: 0 !important;">
        <h2
          style="margin-top: 0 !important; margin-bottom: 0 !important; padding-top: 0 !important; padding-bottom: 0 !important;">
          Traducteur médical basé sur l'IA</h2>
        <h2
          style="margin-top: 0 !important; margin-bottom: 0 !important; padding-top: 0 !important; padding-bottom: 0 !important;">
          fondé sur des sources de référence</h2>
      </div>
    </div>


    <!-- Message d'erreur (masqué par défaut) -->
    <div class="row"
      style="margin-top: 0 !important; margin-bottom: 0 !important; padding-top: 0 !important; padding-bottom: 0 !important;">
      <div class="col-12"
        style="margin-top: 0 !important; margin-bottom: 0 !important; padding-top: 0 !important; padding-bottom: 0 !important;">
        <error-message :message="errorMessage" @dismiss="clearError" />
      </div>
    </div>

    <!-- Panneau principal : saisie | flèche | résultat -->
    <div class="row g-0 translator-panels-row"
      style="margin-top: 0 !important; margin-bottom: 0 !important; padding-top: 0 !important; padding-bottom: 0 !important;">

      <!-- Panneau gauche : saisie -->
      <div class="col-12 col-md translator-panel-column"
        style="margin-top: 0 !important; margin-bottom: 0 !important; padding-top: 0 !important; padding-bottom: 0 !important;">
        <translator-input class="w-100" :is-loading="isLoading" @translate="handleTranslate"
          @translate-file="handleTranslateFile" @mode-changed="translationMode = $event" />
      </div>

      <!-- Flèche indicative (à mi-hauteur) -->
      <div class="col-12 col-md-auto d-flex justify-content-center translator-arrow-column"
        style="margin-top: 0 !important; margin-bottom: 0 !important; padding-top: 0 !important; padding-bottom: 0 !important;">
        <i class="bi bi-arrow-right-circle-fill fs-1 text-primary" aria-hidden="true" title="Traduction"
          style="font-size: 30px;"></i>
      </div>

      <!-- Panneau droit : résultat -->
      <div class="col-12 col-md translator-panel-column"
        style="margin-top: 0 !important; margin-bottom: 0 !important; padding-top: 0 !important; padding-bottom: 0 !important;">
        <translator-output class="w-100" :key="`translator-output-${outputCollapseResetKey}`"
          :translated-text="translatedText" :translation-score="translationScore" :target-lang="currentTargetLang"
          :is-loading="isLoading" :input-text="inputText" :model-id="currentModelId" :direction="currentDirection"
          :autres-traductions="autresTraductions" :detail-items="detailItems" :highlighted-terms="highlightedTerms"
          :translation-mode="translationMode" />
      </div>

    </div>

    <!-- Notifications -->

    <div id="information-messages-container"
      style="margin-top: 0 !important; margin-bottom: 0 !important; padding-top: 0 !important; padding-bottom: 0 !important;">
      <simple-snackbar v-for="(snackbar, index) in store.snackbars" :key="index" :snackbarMessage="snackbar.message"
        :snackbarColor="snackbar.color" :snackbarIcon="snackbar.icon" />
    </div>
  </v-app>
</template>

<script>
import TranslatorInput from './components/TranslatorInput.vue';
import TranslatorOutput from './components/TranslatorOutput.vue';
import ErrorMessage from './components/ErrorMessage.vue';
import storeTranslator from './store-translator.js';
import SimpleSnackbar from '../../common/SimpleSnackbar';

export default {
  name: 'AppTranslateAI',
  components: {
    TranslatorInput,
    TranslatorOutput,
    ErrorMessage,
    SimpleSnackbar
  },
  data() {
    return {
      isLoading: false,
      translatedText: '',
      translationScore: null,
      autresTraductions: [],
      detailItems: [],
      highlightedTerms: [],
      inputText: '',
      currentTargetLang: 'en',
      currentModelId: null,
      currentDirection: null,
      translationMode: null,
      outputCollapseResetKey: 0,
      store: storeTranslator
    }
  },
  computed: {
    errorMessage() {
      return this.store.state.errorMessage
    }
  },
  methods: {
    async handleTranslate(payload) {
      const [modelId, direction] = (payload?.model || '').split('|')
      this.isLoading = true
      this.resetOutputCollapses()
      this.translatedText = ''
      this.translationScore = null
      this.autresTraductions = []
      this.detailItems = []
      this.highlightedTerms = []
      this.currentModelId = modelId || null
      this.currentDirection = direction || null
      this.translationMode = 'text'
      this.store.clearErrorMessage()
      this.inputText = payload.text || ''

      try {
        const result = await this.store.soumettreTermeATraduire({
          text: payload.text,
          model: payload.model
        })

        this.translatedText = result?.translation || ''
        this.translationScore = result?.confidenceScore ?? null
        const topTenTranslations = Array.isArray(result?.topTenTranslations) ? result.topTenTranslations : []
        // Exclure la traduction principale et les doublons, garder uniquement les autres traductions
        this.autresTraductions = topTenTranslations
          .map(item => ({
            terme: item?.translation || '',
            score: item?.score
          }))
          .filter(item => item.terme && item.terme !== this.translatedText)

        const highlights = Array.isArray(result?.highlights) ? result.highlights : []
        this.highlightedTerms = highlights
        this.detailItems = highlights
          .map((highlight, index) => {
            const terminoDetails = Array.isArray(highlight?.terminoDetails) ? highlight.terminoDetails : []
            const term = highlight?.translation || `Terme ${index + 1}`
            const score = highlight?.trustScore ?? null
            const validDetails = terminoDetails.filter(detail => detail?.terminology && detail?.source)
            const synonymes = validDetails.map(detail => detail?.id || '')
            const sources = Object.fromEntries(
              validDetails.map(detail => [detail.terminology, detail.source])
            )
            const termesProches = Object.fromEntries(
              validDetails.map(detail => [
                detail.terminology,
                [...new Set((Array.isArray(detail?.nearTerms) ? detail.nearTerms : []).filter(Boolean))]
              ])
            )
            const themes = terminoDetails
              .map(detail => detail?.theme)
              .filter(Boolean)

            return {
              term,
              score,
              synonymes,
              termesProches,
              sources,
              themes: [...new Set(themes)]
            }
          })
          .filter(item => item.term)
      } catch (err) {
        if (!this.errorMessage) {
          this.store.setErrorMessage('Une erreur est survenue lors de la traduction. Veuillez réessayer.')
        }
      } finally {
        this.isLoading = false
      }
    },
    async handleTranslateFile(payload) {
      this.isLoading = true
      this.resetOutputCollapses()
      this.translationMode = 'file'
      this.store.clearErrorMessage()

      try {
        await this.store.soumettreFichierATraduire({
          file: payload.file,
          modelId: payload.modelId,
          direction: payload.direction,
          outputColumns: payload.outputColumns
        })
      } catch (err) {
        if (!this.errorMessage) {
          this.store.setErrorMessage('Une erreur est survenue lors de la traduction du fichier. Veuillez réessayer.')
        }
      } finally {
        this.isLoading = false
      }
    },
    clearError() {
      this.store.clearErrorMessage()
    },
    resetOutputCollapses() {
      this.outputCollapseResetKey += 1
    }
  }
}
</script>

<style scoped>
#app-translate-ai {
  background-color: transparent;
}

::v-deep #information-messages-container {
  position: fixed;
  box-sizing: border-box;
  bottom: 0;
  right: 0;
  z-index: 1000;
  height: auto;
  width: 400px;
  background-color: transparent;
}

::v-deep #information-messages-container p {
  font-size: 14px;
}


::v-deep .v-application--wrap {
  min-height: auto;
}

::v-deep .output-text-display {
  overflow-y: auto !important;
  background-color: #e9ecef !important;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.6;
}

.translator-panels-row {
  align-items: flex-start;
}

.translator-panel-column {
  display: flex;
  align-items: flex-start;
}

.translator-arrow-column {
  align-self: flex-start;
  align-items: flex-start;
  width: 4rem;
  min-width: 4rem;
  max-width: 4rem;
  padding-left: 2rem;
  padding-right: 2rem;
  padding-top: 10rem;
}

@media (max-width: 767.98px) {
  .translator-arrow-column {
    justify-content: center;
    padding-top: 0;
    padding-bottom: 1rem;
  }
}

.th-panel-header {
  background-color: #1d71b8 !important;
  color: #fff !important;
}

.th-ans-style {
  background-color: var(--primary);
  color: white;
}

::v-deep .th-ans-style {
  background-color: var(--primary);
  color: white;
}

#btn-traduire {
  display: block;
  margin-left: auto;
}


.force-padding-0 {
  padding: 0 !important;
}

.force-margin-0 {
  margin: 0 !important;
}
</style>
